import React, { useState, useMemo } from 'react';
import { Mail, Phone, Star } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Flat-hierarchy network visual for Wisewit Team Kernel.
 * Founders sit in the center, every other member orbits around them.
 * Edges drawn:
 *  - Solid spokes from EVERY founder to EVERY other member (shows everyone is directly connected to the top)
 *  - Light ring edges between adjacent orbit members
 *  - Subtle chord edges between every Nth member for "mesh" feel
 * Hovering a node fades non-incident edges; clicking opens a member-info popover.
 */
export const TeamKernelNetwork = ({ kernel }) => {
  const [hoverIdx, setHoverIdx] = useState(null);
  const [openIdx, setOpenIdx] = useState(null);

  const { centerNodes, orbitNodes } = useMemo(() => {
    const layers = kernel?.layers || [];
    if (!layers.length) return { centerNodes: [], orbitNodes: [] };
    // Treat the first layer as centre (founders).
    const center = (layers[0].members || []).map((m, i) => ({ ...m, layer: layers[0].title, layerIdx: 0, key: `c-${i}` }));
    const orbit = [];
    layers.slice(1).forEach((layer, lIdx) => {
      (layer.members || []).forEach((m, mIdx) => {
        orbit.push({ ...m, layer: layer.title, layerIdx: lIdx + 1, key: `o-${lIdx}-${mIdx}` });
      });
    });
    return { centerNodes: center, orbitNodes: orbit };
  }, [kernel]);

  // Layer palette (consistent with the prior version, but used purely for tint, not for hierarchy)
  const LAYER_TINTS = [
    '#7C3AED', // founders – violet
    '#2563EB', // blue
    '#059669', // emerald
    '#D97706', // amber
    '#E11D48', // rose
    '#0891B2', // cyan
  ];

  if (!centerNodes.length && !orbitNodes.length) {
    return <p className="text-center text-sm text-muted-foreground py-8">No members added yet.</p>;
  }

  const SIZE = 700;
  const cx = SIZE / 2;
  const cy = SIZE / 2;
  const orbitRadius = orbitNodes.length > 0 ? Math.min(280, 100 + orbitNodes.length * 8) : 120;
  const centerRadius = centerNodes.length > 1 ? 70 : 0;
  const nodeR = 30;

  // Positions
  const centerPositions = centerNodes.map((n, i) => {
    if (centerNodes.length === 1) return { ...n, x: cx, y: cy };
    const a = (i / centerNodes.length) * 2 * Math.PI - Math.PI / 2;
    return { ...n, x: cx + centerRadius * Math.cos(a), y: cy + centerRadius * Math.sin(a) };
  });
  const orbitPositions = orbitNodes.map((n, i) => {
    const a = (i / Math.max(1, orbitNodes.length)) * 2 * Math.PI - Math.PI / 2;
    return { ...n, x: cx + orbitRadius * Math.cos(a), y: cy + orbitRadius * Math.sin(a) };
  });

  // All nodes for hover/click
  const allNodes = [...centerPositions, ...orbitPositions];
  const isIncident = (a, b) => {
    if (hoverIdx == null && openIdx == null) return true;
    const focus = hoverIdx != null ? hoverIdx : openIdx;
    return a === focus || b === focus;
  };

  // Edges
  const founderSpokes = [];
  centerPositions.forEach((c, ci) => {
    orbitPositions.forEach((o, oi) => {
      founderSpokes.push({ a: ci, b: centerPositions.length + oi, x1: c.x, y1: c.y, x2: o.x, y2: o.y });
    });
  });
  // Connect founders to each other too (when multiple)
  for (let i = 0; i < centerPositions.length; i++) {
    for (let j = i + 1; j < centerPositions.length; j++) {
      founderSpokes.push({ a: i, b: j, x1: centerPositions[i].x, y1: centerPositions[i].y, x2: centerPositions[j].x, y2: centerPositions[j].y });
    }
  }
  // Ring edges between adjacent orbit nodes — emphasises mesh
  const ringEdges = [];
  for (let i = 0; i < orbitPositions.length; i++) {
    const a = orbitPositions[i];
    const b = orbitPositions[(i + 1) % orbitPositions.length];
    if (orbitPositions.length < 2) break;
    ringEdges.push({ a: centerPositions.length + i, b: centerPositions.length + ((i + 1) % orbitPositions.length), x1: a.x, y1: a.y, x2: b.x, y2: b.y });
  }
  // Sparse cross chords every 3rd to create a flat-net feel without clutter
  const chordEdges = [];
  if (orbitPositions.length >= 4) {
    for (let i = 0; i < orbitPositions.length; i++) {
      const j = (i + Math.max(2, Math.floor(orbitPositions.length / 3))) % orbitPositions.length;
      if (i < j) {
        const a = orbitPositions[i];
        const b = orbitPositions[j];
        chordEdges.push({ a: centerPositions.length + i, b: centerPositions.length + j, x1: a.x, y1: a.y, x2: b.x, y2: b.y });
      }
    }
  }

  const focused = openIdx != null ? allNodes[openIdx] : (hoverIdx != null ? allNodes[hoverIdx] : null);

  return (
    <div className="relative w-full" data-testid={`network-${kernel.id}`}>
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 mb-3 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full" style={{ background: LAYER_TINTS[0] }} />
          {kernel.layers?.[0]?.title || 'Centre'}
        </span>
        {(kernel.layers || []).slice(1).map((l, i) => (
          <span key={i} className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full" style={{ background: LAYER_TINTS[(i + 1) % LAYER_TINTS.length] }} />
            {l.title}
          </span>
        ))}
        <span className="ml-auto italic">Flat mesh — every member is directly linked to the top.</span>
      </div>

      <div className="rounded-xl border bg-gradient-to-br from-slate-50 to-white dark:from-zinc-900 dark:to-zinc-900/60 overflow-x-auto">
        <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="w-full" style={{ minHeight: 520 }} preserveAspectRatio="xMidYMid meet">
          {/* Soft background grid pulse */}
          <defs>
            <radialGradient id="ctrGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={LAYER_TINTS[0]} stopOpacity="0.15" />
              <stop offset="100%" stopColor={LAYER_TINTS[0]} stopOpacity="0" />
            </radialGradient>
          </defs>
          <circle cx={cx} cy={cy} r={orbitRadius + 30} fill="url(#ctrGlow)" />
          <circle cx={cx} cy={cy} r={orbitRadius} fill="none" stroke="currentColor" strokeOpacity="0.08" strokeDasharray="4 6" />

          {/* Chord edges (subtle) */}
          {chordEdges.map((e, i) => (
            <line key={`ch-${i}`}
              x1={e.x1} y1={e.y1} x2={e.x2} y2={e.y2}
              stroke="#94A3B8"
              strokeWidth={isIncident(e.a, e.b) ? 1 : 0.5}
              strokeOpacity={isIncident(e.a, e.b) ? 0.25 : 0.08}
              strokeDasharray="2 4"
            />
          ))}
          {/* Ring edges */}
          {ringEdges.map((e, i) => (
            <line key={`r-${i}`}
              x1={e.x1} y1={e.y1} x2={e.x2} y2={e.y2}
              stroke="#94A3B8"
              strokeWidth={isIncident(e.a, e.b) ? 1.5 : 1}
              strokeOpacity={isIncident(e.a, e.b) ? 0.5 : 0.18}
            />
          ))}
          {/* Founder spokes (strong) */}
          {founderSpokes.map((e, i) => (
            <line key={`s-${i}`}
              x1={e.x1} y1={e.y1} x2={e.x2} y2={e.y2}
              stroke={LAYER_TINTS[0]}
              strokeWidth={isIncident(e.a, e.b) ? 2 : 1.2}
              strokeOpacity={isIncident(e.a, e.b) ? 0.55 : 0.18}
            />
          ))}

          {/* Nodes */}
          {allNodes.map((n, idx) => {
            const tint = LAYER_TINTS[n.layerIdx % LAYER_TINTS.length];
            const focusedNode = hoverIdx === idx || openIdx === idx;
            const r = n.layerIdx === 0 ? nodeR + 6 : nodeR;
            return (
              <g key={n.key}
                transform={`translate(${n.x}, ${n.y})`}
                onMouseEnter={() => setHoverIdx(idx)}
                onMouseLeave={() => setHoverIdx(null)}
                onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
                style={{ cursor: 'pointer' }}
                data-testid={`network-node-${idx}`}
              >
                <circle r={r + 4} fill={tint} fillOpacity={focusedNode ? 0.25 : 0.12} />
                <circle r={r} fill="white" stroke={tint} strokeWidth={focusedNode ? 3 : 2} />
                {n.photo ? (
                  <>
                    <defs>
                      <clipPath id={`clip-${n.key}`}><circle r={r - 2} /></clipPath>
                    </defs>
                    <image
                      href={`${API_URL}/api/files/${n.photo}`}
                      x={-(r - 2)} y={-(r - 2)} width={(r - 2) * 2} height={(r - 2) * 2}
                      clipPath={`url(#clip-${n.key})`}
                      preserveAspectRatio="xMidYMid slice"
                    />
                  </>
                ) : (
                  <text x={0} y={5} textAnchor="middle" fontSize={r * 0.7} fontWeight="700" fill={tint}>
                    {(n.name || '?').charAt(0).toUpperCase()}
                  </text>
                )}
                {n.layerIdx === 0 && (
                  <g transform={`translate(${r - 6},${-r + 2})`}>
                    <circle r="9" fill="#FBBF24" stroke="white" strokeWidth="2" />
                    <g transform="translate(-5.5,-5.5) scale(0.65)">
                      <Star size={16} className="text-white" />
                    </g>
                  </g>
                )}
                <text x={0} y={r + 16} textAnchor="middle" fontSize="11" fontWeight="600" fill="currentColor">
                  {(n.name || 'Unnamed').slice(0, 20)}
                </text>
                <text x={0} y={r + 30} textAnchor="middle" fontSize="9.5" fill="currentColor" fillOpacity="0.55">
                  {n.layer}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Side card with focused member */}
      {focused && (
        <div className="absolute right-4 top-14 w-64 rounded-lg border bg-card shadow-lg p-3 z-10" data-testid="network-focused-card">
          <div className="flex items-center gap-2">
            {focused.photo ? (
              <img src={`${API_URL}/api/files/${focused.photo}`} alt={focused.name} className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-sm font-bold">{(focused.name || '?').charAt(0).toUpperCase()}</div>
            )}
            <div className="min-w-0">
              <p className="font-semibold text-sm truncate">{focused.name || 'Unnamed'}</p>
              <p className="text-xs text-muted-foreground truncate">{focused.layer}</p>
            </div>
          </div>
          {focused.email && (
            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1 truncate">
              <Mail className="w-3 h-3 shrink-0" /> {focused.email}
            </p>
          )}
          {focused.phone && (
            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
              <Phone className="w-3 h-3 shrink-0" /> {focused.phone}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamKernelNetwork;
